import { useCallback, useContext, useEffect, useState } from 'react'
import PageContent from '../../ui/PageContent'
import { DataTable } from '../../ui/DataTable'
import api from '../../api'
import { Organization } from '../../types'
import { useTranslation } from 'react-i18next'
import { OrganizationContext } from '../../contexts'
import { Button } from '../../ui'
import { PlusIcon } from '../../ui/icons'
import { snakeToTitle } from '../../utils'
import OrganizationCreateModal from './OrganizationCreateModal'

export default function Organizations() {
    const { t } = useTranslation()
    const [currentOrg] = useContext(OrganizationContext)
    const [organizations, setOrganizations] = useState<Organization[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isCreateOpen, setIsCreateOpen] = useState(false)

    const loadOrganizations = useCallback(async () => {
        await api.organizations.all()
            .then(setOrganizations)
            .finally(() => setIsLoading(false))
    }, [])

    useEffect(() => {
        void loadOrganizations()
    }, [loadOrganizations])

    return (
        <PageContent
            title={t('organizations')}
            actions={
                <Button icon={<PlusIcon />} onClick={() => setIsCreateOpen(true)}>
                    {t('new_organization')}
                </Button>
            }
        >
            <DataTable<Organization>
                isLoading={isLoading}
                items={organizations}
                columns={[
                    { key: 'username', title: t('name') },
                    { key: 'domain', title: t('domain') },
                    {
                        key: 'type',
                        title: t('type'),
                        cell: ({ item }) => snakeToTitle(item.type),
                    },
                    {
                        key: 'actions',
                        title: '',
                        align: 'end',
                        cell: ({ item }) => item.id !== currentOrg.id
                            ? <Button
                                size="small"
                                variant="secondary"
                                onClick={async () => await api.organizations.switch(item.id)}
                            >
                                {t('switch_organization')}
                            </Button>
                            : <span className="tag">{t('current')}</span>,
                    },
                ]}
            />
            <OrganizationCreateModal
                open={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
                onCreated={() => { void loadOrganizations() }}
            />
        </PageContent>
    )
}
